import {
  declareWhatsAppSentResponseSchema,
  prepareWhatsAppResponseSchema,
  replyWhatsAppResponseSchema,
  sendWhatsAppResponseSchema,
  whatsAppTemplatesResponseSchema,
  type PrepareWhatsAppBody,
  type PrepareWhatsAppResponse,
  type ReplyWhatsAppBody,
  type SendWhatsAppBody,
} from "@gioia365/contracts";
import { queryOptions, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { ApiRequestError, apiRequest } from "../api/http";
import { conversationKeys } from "../conversations/conversation-keys";
import { leadKeys } from "../leads/lead-queries";
import { tabKey, type TabContext, type TabKey } from "../session/tab-context";

// Le chiamate del WhatsApp a mano (piano M6, R1, Task 4a; rotte dei Task 3a e 3b, contratti in `packages/contracts/src/whatsapp.ts`):
// - i modelli dell'azienda e la riga «scrivi STOP» (`GET /api/whatsapp/templates`), che la finestra riempie nel browser;
// - «Apri WhatsApp» (`POST /api/leads/:leadId/whatsapp/prepare`), che registra il messaggio preparato con il testo messo nel link;
// - «Sì, inviato» (`POST /api/whatsapp/messages/:messageId/declare-sent`), soltanto l'autore, una volta;
// - dal piano M6, R2 (Task 9b), «Invia il WhatsApp» dal WhatsApp ufficiale (`POST /api/leads/:leadId/whatsapp/send`, Task 9a);
// - dal piano M6, R3 (passo 12b), «Rispondi su WhatsApp» da «Conversazioni» (`POST /api/leads/:leadId/whatsapp/reply`, passo 12a).
// Dopo ogni scrittura la Timeline del Lead si rilegge: il messaggio è un suo elemento (D3, questione 402). Un messaggio che conta
// per il tetto (dichiarato inviato, preso in carico da Meta) rilegge anche la scheda, che dice quanti WhatsApp il Lead ha già
// ricevuto oggi (`whatsapp.todayCount`, questione 413).

export const whatsAppKeys = {
  templates: (tab: TabKey) => ["scheda", tab, "whatsapp", "templates"] as const,
};

/**
 * I sei modelli e la riga «scrivi STOP» dell'azienda. Un minuto senza rileggerli: la scheda li legge in anticipo quando «WhatsApp»
 * è acceso, così la finestra si apre già con il modello riempito (velocità: 150 ms, `ACCEPTANCE_CRITERIA.md`).
 */
export function whatsAppTemplatesQuery(tab: TabContext) {
  return queryOptions({
    queryKey: whatsAppKeys.templates(tabKey(tab)),
    queryFn: ({ signal }) => apiRequest("GET", "/api/whatsapp/templates", { schema: whatsAppTemplatesResponseSchema, tab, signal }),
    staleTime: 60_000,
  });
}

/**
 * I rifiuti dopo cui `whatsapp` della scheda non dice più il vero: la scheda si rilegge e spegne «WhatsApp» con il motivo; dopo il tetto
 * di WhatsApp Agile (`409 whatsapp_daily_cap`, R9) il conteggio di oggi.
 */
const CARD_STALE_CODES: ReadonlySet<string> = new Set(["whatsapp_blocked", "lead_not_found", "permission_denied", "whatsapp_daily_cap"]);

/** «Apri WhatsApp»: il messaggio preparato, con il testo esatto che il link porta. */
export function usePrepareWhatsApp(tab: TabContext, leadId: string, view: string | null) {
  const queryClient = useQueryClient();
  const tabPart = tabKey(tab);
  return useMutation({
    mutationFn: (body: PrepareWhatsAppBody) =>
      apiRequest("POST", `/api/leads/${encodeURIComponent(leadId)}/whatsapp/prepare`, { schema: prepareWhatsAppResponseSchema, body, tab }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: leadKeys.timelines(tabPart, leadId, view) }),
    onError: (error) => {
      if (error instanceof ApiRequestError && CARD_STALE_CODES.has(error.code)) void queryClient.invalidateQueries({ queryKey: leadKeys.card(tabPart, leadId, view), exact: true });
    },
  });
}

/** Il messaggio è già dichiarato inviato (un'altra scheda, la Timeline): la dichiarazione che si voleva c'è già. */
export function alreadyDeclared(error: unknown): boolean {
  return error instanceof ApiRequestError && error.code === "invalid_state" && error.details?.status === "declared_sent";
}

/** «Sì, inviato», dalla finestra o dalla Timeline. Il messaggio dichiarato non si riusa più per un'altra apertura. */
export function useDeclareWhatsAppSent(tab: TabContext, view: string | null) {
  const queryClient = useQueryClient();
  const tabPart = tabKey(tab);
  return useMutation({
    mutationFn: (messageId: string) =>
      apiRequest("POST", `/api/whatsapp/messages/${encodeURIComponent(messageId)}/declare-sent`, { schema: declareWhatsAppSentResponseSchema, tab }),
    onSuccess: ({ message }) => {
      forgetPreparedMessage(queryClient, message.id);
      void queryClient.invalidateQueries({ queryKey: leadKeys.timelines(tabPart, message.leadId, view) });
      void queryClient.invalidateQueries({ queryKey: leadKeys.card(tabPart, message.leadId, view), exact: true });
    },
    onError: (error, messageId) => {
      if (alreadyDeclared(error)) forgetPreparedMessage(queryClient, messageId);
    },
  });
}

/**
 * «Invia il WhatsApp» dal WhatsApp ufficiale (piano M6, R2, Task 9b): il server ricontrolla la scelta e risponde con il messaggio
 * preso in carico da Meta (o rifiutato da Meta), oppure senza messaggio quando resta a mano. Ognuna di queste risposte chiude la
 * richiesta: il suo identificativo si dimentica (`sendRequestIdFor`). Un messaggio scritto rilegge la Timeline, dove compare, e la
 * scheda (il conteggio di oggi); un messaggio rimasto a mano rilegge la scheda e i modelli, che non dicevano più il vero (il WhatsApp
 * di chi scrive, l'approvazione di Meta). Un rifiuto dopo cui la scheda non dice più il vero la rilegge, come per la preparazione.
 */
export function useSendWhatsApp(tab: TabContext, leadId: string, view: string | null) {
  const queryClient = useQueryClient();
  const tabPart = tabKey(tab);
  return useMutation({
    mutationFn: (body: SendWhatsAppBody) =>
      apiRequest("POST", `/api/leads/${encodeURIComponent(leadId)}/whatsapp/send`, { schema: sendWhatsAppResponseSchema, body, tab }),
    onSuccess: (response, body) => {
      forgetSendRequest(queryClient, body.clientRequestId);
      void queryClient.invalidateQueries({ queryKey: leadKeys.card(tabPart, leadId, view), exact: true });
      if (response.messageId === null) void queryClient.invalidateQueries({ queryKey: whatsAppKeys.templates(tabPart) });
      else void queryClient.invalidateQueries({ queryKey: leadKeys.timelines(tabPart, leadId, view) });
    },
    onError: (error) => {
      if (error instanceof ApiRequestError && CARD_STALE_CODES.has(error.code)) void queryClient.invalidateQueries({ queryKey: leadKeys.card(tabPart, leadId, view), exact: true });
    },
  });
}

/** I rifiuti dopo cui la riga di «Conversazioni → WhatsApp» non dice più il vero (il messaggio, il Lead o il telefono non ci sono più). */
const ROW_STALE_CODES: ReadonlySet<string> = new Set(["not_found", "lead_not_found", "validation_failed", "whatsapp_blocked", "permission_denied"]);

/**
 * «Rispondi su WhatsApp» (piano M6, R3, passo 12b): il server ricontrolla tutto e risponde con il messaggio preso in carico da Meta
 * (o rifiutato da Meta), senza messaggio quando resta a mano, oppure, con «Apri WhatsApp» (`manual`), con il messaggio preparato e il
 * suo link. Una risposta con un esito chiude la richiesta (`replyRequestIdFor`), tranne quella a mano: lo stesso testo ritrova lo
 * stesso messaggio preparato finché non è dichiarato inviato (ruling E8, come la preparazione di R1). Dopo, la Timeline del Lead, la
 * scheda (il conteggio di oggi), la lista dei WhatsApp e il numero da leggere si rileggono; un messaggio rimasto a mano rilegge i
 * modelli. Un rifiuto dopo cui la riga non dice più il vero rilegge la lista e la scheda.
 */
export function useReplyWhatsApp(tab: TabContext, leadId: string, view: string | null) {
  const queryClient = useQueryClient();
  const tabPart = tabKey(tab);
  const refreshConversations = () => {
    void queryClient.invalidateQueries({ queryKey: conversationKeys.whatsappLists(tabPart) });
    void queryClient.invalidateQueries({ queryKey: conversationKeys.whatsappUnreadAll(tabPart) });
  };
  return useMutation({
    mutationFn: (body: ReplyWhatsAppBody) =>
      apiRequest("POST", `/api/leads/${encodeURIComponent(leadId)}/whatsapp/reply`, { schema: replyWhatsAppResponseSchema, body, tab }),
    onSuccess: (response, body) => {
      if (body.manual !== true) forgetSendRequest(queryClient, body.clientRequestId);
      refreshConversations();
      void queryClient.invalidateQueries({ queryKey: leadKeys.card(tabPart, leadId, view), exact: true });
      if (response.messageId === null) void queryClient.invalidateQueries({ queryKey: whatsAppKeys.templates(tabPart) });
      else void queryClient.invalidateQueries({ queryKey: leadKeys.timelines(tabPart, leadId, view) });
    },
    onError: (error) => {
      if (!(error instanceof ApiRequestError)) return;
      if (ROW_STALE_CODES.has(error.code)) refreshConversations();
      if (CARD_STALE_CODES.has(error.code)) void queryClient.invalidateQueries({ queryKey: leadKeys.card(tabPart, leadId, view), exact: true });
    },
  });
}

// ---------------------------------------------------------------------------------------------------------------
// Il messaggio preparato da riusare (ruling E8): riaprire la finestra e premere di nuovo «Apri WhatsApp» con lo stesso cellulare,
// lo stesso testo e la stessa chiamata riapre lo stesso messaggio, senza una seconda preparazione né un secondo elemento nella
// Timeline, finché non è dichiarato inviato. La memoria si scrive al clic, con la preparazione ancora in viaggio: una finestra
// chiusa e riaperta subito aspetta la stessa risposta invece di prepararne un'altra; una preparazione rifiutata si dimentica. La
// memoria è della cache della pagina (una per scheda del browser): sparisce con lei.

export type PreparedMessage = Pick<PrepareWhatsAppResponse, "messageId" | "link">;

/** Un messaggio preparato o in preparazione: la risposta che arriverà, e quella già arrivata. */
export type PreparedEntry = { promise: Promise<PreparedMessage>; current: () => PreparedMessage | null };

const preparedByClient = new WeakMap<QueryClient, Map<string, PreparedEntry>>();

function preparedOf(queryClient: QueryClient): Map<string, PreparedEntry> {
  let prepared = preparedByClient.get(queryClient);
  if (!prepared) {
    prepared = new Map();
    preparedByClient.set(queryClient, prepared);
  }
  return prepared;
}

type PreparedKey = { tab: TabContext; leadId: string; callId: string | null; phoneId: string; text: string };

const preparedKey = (key: PreparedKey) => JSON.stringify([tabKey(key.tab), key.leadId, key.callId, key.phoneId, key.text]);

/**
 * Il messaggio con questo cellulare, questo testo e questa chiamata: quello già preparato o in preparazione, se non è ancora
 * dichiarato inviato, altrimenti uno nuovo, che `prepare` comincia adesso.
 */
export function preparedMessageFor(queryClient: QueryClient, key: PreparedKey, prepare: () => Promise<PreparedMessage>): PreparedEntry {
  const prepared = preparedOf(queryClient);
  const id = preparedKey(key);
  const known = prepared.get(id);
  if (known) return known;
  let settled: PreparedMessage | null = null;
  const promise = prepare().then(
    (message) => {
      settled = message;
      return message;
    },
    (error: unknown) => {
      // Finché è in memoria nessun'altra preparazione parte con la stessa chiave: quella sotto `id` è questa.
      prepared.delete(id);
      throw error;
    },
  );
  const entry: PreparedEntry = { promise, current: () => settled };
  prepared.set(id, entry);
  return entry;
}

function forgetPreparedMessage(queryClient: QueryClient, messageId: string): void {
  const prepared = preparedOf(queryClient);
  for (const [key, entry] of prepared) if (entry.current()?.messageId === messageId) prepared.delete(key);
}

// ---------------------------------------------------------------------------------------------------------------
// L'identificativo della richiesta di «Invia il WhatsApp» (ruling E4, revisione finale di R2): uno per Lead, modello e cellulare,
// nella cache della pagina (una per scheda del browser) come il messaggio preparato, non nella finestra. Dopo una rete caduta il
// server può aver scritto il messaggio: la finestra si chiude, e riaperta manda la stessa richiesta, che ritrova quel messaggio invece
// di scriverne un secondo. Si dimentica soltanto a una risposta del server con il suo esito (preso in carico, rifiutato da Meta,
// tenuto a mano: `useSendWhatsApp`). Un errore, anche un rifiuto del server, lo tiene: un controllo che viene prima di quello della
// stessa richiesta (il Lead intanto fuori dal perimetro) può nascondere un messaggio già scritto, e tenerlo non costa niente, perché
// il server riconosce la stessa richiesta. Due schede del browser hanno due cache: due identificativi (accettato).

const sendRequestsByClient = new WeakMap<QueryClient, Map<string, string>>();

function sendRequestsOf(queryClient: QueryClient): Map<string, string> {
  let requests = sendRequestsByClient.get(queryClient);
  if (!requests) {
    requests = new Map();
    sendRequestsByClient.set(queryClient, requests);
  }
  return requests;
}

type SendRequestKey = { tab: TabContext; leadId: string; templateKey: string; phoneId: string };

/** L'identificativo della richiesta per questo Lead, questo modello e questo cellulare: quello ancora aperto, oppure uno nuovo. */
export function sendRequestIdFor(queryClient: QueryClient, key: SendRequestKey): string {
  const requests = sendRequestsOf(queryClient);
  const id = JSON.stringify([tabKey(key.tab), key.leadId, key.templateKey, key.phoneId]);
  const known = requests.get(id);
  if (known !== undefined) return known;
  const clientRequestId = crypto.randomUUID();
  requests.set(id, clientRequestId);
  return clientRequestId;
}

function forgetSendRequest(queryClient: QueryClient, clientRequestId: string): void {
  const requests = sendRequestsOf(queryClient);
  for (const [key, value] of requests) if (value === clientRequestId) requests.delete(key);
}

/**
 * Che cosa chiede una risposta (piano M6, R3, passo 12b): un testo libero dal WhatsApp ufficiale (`text:<testo>`), un modello (`template:<chiave>`)
 * oppure «Apri WhatsApp» con quel testo (`manual:<testo>`): per la stessa risposta lo stesso testo ritrova lo stesso messaggio, un testo corretto è
 * una richiesta nuova.
 */
type ReplyRequestKey = { tab: TabContext; leadId: string; inReplyTo: string; kind: string };

/**
 * L'identificativo della richiesta di «Rispondi su WhatsApp» (ruling E4, come «Invia il WhatsApp»): uno per Lead, messaggio a cui si
 * risponde e tipo di risposta, nella cache della pagina, finché il server non risponde con il suo esito. Dopo una rete caduta il
 * nuovo clic, anche a finestra riaperta, ritrova il messaggio che il server ha già scritto invece di mandarne un secondo.
 */
export function replyRequestIdFor(queryClient: QueryClient, key: ReplyRequestKey): string {
  const requests = sendRequestsOf(queryClient);
  const id = JSON.stringify(["reply", tabKey(key.tab), key.leadId, key.inReplyTo, key.kind]);
  const known = requests.get(id);
  if (known !== undefined) return known;
  const clientRequestId = crypto.randomUUID();
  requests.set(id, clientRequestId);
  return clientRequestId;
}

/** Una risposta a mano dichiarata inviata: lo stesso testo, dopo, è una risposta nuova. */
export function forgetReplyRequest(queryClient: QueryClient, clientRequestId: string): void {
  forgetSendRequest(queryClient, clientRequestId);
}
